import { useEffect, useRef, useState } from 'react';

import { EmptyState } from '@/components/feedback/EmptyState';
import { InboxIcon } from '@/components/icons';
import { ErrorNotice, LoadingState, PageHeading } from '@/components/portal/PortalPage';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';

import { useInbox, useMarkRead } from './useMessages';
import type { InboxMessage } from './types';

/** Date et heure, en toutes lettres. */
const formatSentAt = (value: string): string =>
  new Date(value).toLocaleString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

/**
 * Corps du message, rendu en paragraphes.
 *
 * Le serveur transmet du **texte brut**, et React l'échappe : un message ne
 * peut donc pas injecter de balise dans cette page. C'est voulu de bout en
 * bout — la même contrainte s'applique au corps de l'e-mail.
 */
const MessageBody = ({ body }: { body: string }) => (
  <div className="text-navy-900 space-y-3 text-sm leading-6">
    {body
      .split(/\n{2,}/)
      .map((paragraph) => paragraph.trim())
      .filter((paragraph) => paragraph !== '')
      .map((paragraph, index) => (
        /* Clé par position : le texte serait la seule autre clé possible, et
           deux paragraphes identiques dans un même message sont légitimes.
           La liste ne se réordonne jamais — elle est recalculée en entier à
           chaque rendu du corps. */
        <p key={`${String(index)}-${paragraph.slice(0, 24)}`} className="whitespace-pre-line">
          {paragraph}
        </p>
      ))}
  </div>
);

/**
 * Une ligne de la boîte, dépliable.
 *
 * ── Pourquoi déplier sur place, et non une page par message ──────────────
 * Ces messages sont courts et peu nombreux. Une route dédiée imposerait un
 * aller-retour pour lire trois lignes, et un retour en arrière pour passer au
 * suivant. Le dépliage garde la liste sous les yeux.
 *
 * L'ouverture marque le message comme lu : c'est le geste qui signifie
 * « je l'ai vu », et demander en plus un clic sur « marquer comme lu » serait
 * une corvée sans objet.
 */
const MessageRow = ({
  message,
  expanded,
  onToggle,
}: {
  message: InboxMessage;
  expanded: boolean;
  onToggle: () => void;
}) => {
  const unread = message.readAt === null;

  return (
    <li className="portal-surface overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="focus-visible:outline-accent-300 flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-slate-50 focus-visible:outline-2 focus-visible:-outline-offset-2"
      >
        {/* Pastille de non-lu : repérable d'un coup d'œil, et doublée d'un
            libellé pour les lecteurs d'écran — une couleur seule ne dit rien. */}
        <span
          aria-hidden
          className={`mt-1.5 size-2 shrink-0 rounded-full ${unread ? 'bg-accent-600' : 'bg-transparent'}`}
        />

        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-baseline gap-x-2">
            <span
              className={`text-navy-900 text-sm wrap-anywhere ${unread ? 'font-semibold' : 'font-medium'}`}
            >
              {message.subject}
            </span>
            {unread ? <Badge tone="accent">Non lu</Badge> : null}
          </span>
          <span className="text-ink-500 mt-0.5 block text-xs">
            {message.from ? `${message.from} · ` : ''}
            {formatSentAt(message.sentAt)}
          </span>
        </span>
      </button>

      {expanded ? (
        <div className="border-navy-100 border-t px-4 py-4">
          <MessageBody body={message.body} />
        </div>
      ) : null}
    </li>
  );
};

/**
 * La boîte de réception du consultant.
 *
 * ── Aucun bouton « répondre », et ce n'est pas un oubli ──────────────────
 * L'ESN écrit, le consultant lit. Proposer de répondre créerait une file que
 * personne n'est chargé de relever : un consultant qui écrirait sans jamais
 * obtenir de retour serait plus mal servi qu'avec un simple e-mail. L'encart
 * de bas de page le dit explicitement, plutôt que de laisser chercher un
 * bouton qui n'existe pas.
 */
export const MessagesPage = () => {
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const query = useInbox(page);
  const markRead = useMarkRead();

  /* Les messages déjà signalés comme lus pendant cette session. Sans cela, le
     rafraîchissement automatique de la liste relancerait la mutation sur un
     message resté déplié. */
  const marked = useRef(new Set<string>());

  useEffect(() => {
    if (expandedId === null || marked.current.has(expandedId)) return;

    const message = query.data?.items.find((item) => item.id === expandedId);
    if (!message || message.readAt !== null) return;

    marked.current.add(expandedId);
    markRead.mutate(expandedId);
  }, [expandedId, query.data, markRead]);

  if (query.isPending) {
    return <LoadingState>Chargement de vos messages…</LoadingState>;
  }

  if (query.isError) {
    return (
      <ErrorNotice
        onRetry={() => {
          void query.refetch();
        }}
      >
        {query.error.message}
      </ErrorNotice>
    );
  }

  const { items, meta } = query.data;

  return (
    <div className="portal-page">
      <PageHeading
        title="Mes messages"
        description={
          meta.unread === 0
            ? 'Les informations que votre ESN vous adresse.'
            : `${String(meta.unread)} message${meta.unread > 1 ? 's' : ''} non lu${meta.unread > 1 ? 's' : ''}.`
        }
        icon={<InboxIcon />}
      />

      {items.length === 0 ? (
        <EmptyState
          className="my-8"
          icon={<InboxIcon />}
          title="Aucun message"
          description="Votre interlocuteur ubeefy vous écrira ici pour les informations qui concernent vos missions."
        />
      ) : (
        <>
          <ul className="space-y-2">
            {items.map((message) => (
              <MessageRow
                key={message.id}
                message={message}
                expanded={expandedId === message.id}
                onToggle={() => {
                  setExpandedId((current) => (current === message.id ? null : message.id));
                }}
              />
            ))}
          </ul>

          {meta.totalPages > 1 ? (
            <nav
              aria-label="Pagination des messages"
              className="mt-4 flex items-center justify-between gap-3"
            >
              <Button
                size="sm"
                variant="secondary"
                disabled={!meta.hasPreviousPage}
                onClick={() => {
                  setPage((current) => Math.max(1, current - 1));
                }}
              >
                Précédents
              </Button>
              <span className="text-ink-500 text-xs">
                Page {meta.page} sur {meta.totalPages}
              </span>
              <Button
                size="sm"
                variant="secondary"
                disabled={!meta.hasNextPage}
                onClick={() => {
                  setPage((current) => current + 1);
                }}
              >
                Suivants
              </Button>
            </nav>
          ) : null}

          <p className="text-ink-500 mt-6 text-xs">
            Cette boîte est en lecture seule. Pour répondre ou poser une question, écrivez
            directement à votre interlocuteur habituel.
          </p>
        </>
      )}
    </div>
  );
};
